import { useEffect, useState, useCallback } from 'react'
import { apiService } from '@/service/api'
import JsonViewer from '@/routes/GraphEditor/JsonViewer'
import { Loader2 } from 'lucide-react'

interface TimeseriesPreviewProps {
  docId: string
}

interface TimeseriesData {
  metadata: unknown
  tags: unknown
}

const TimeseriesPreview = ({ docId }: TimeseriesPreviewProps) => {
  const [data, setData] = useState<TimeseriesData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!docId) return

      setLoading(true)
      setError(null)

      try {
        const [metadata, tags] = await Promise.all([
          apiService.getTimeseriesMetadata(docId),
          apiService.getTimeseriesTags(docId),
        ])

        setData({ metadata, tags })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load timeseries data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [docId])

  // Stop wheel events from propagating to tldraw canvas (for board view)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        {error}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
        No data available
      </div>
    )
  }

  return (
    <div className="h-full" onWheelCapture={handleWheel}>
      <JsonViewer data={data} title="Timeseries Data" />
    </div>
  )
}

export default TimeseriesPreview
